import React, { useState, useRef } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Paper, 
  Link, 
  CircularProgress,
  Alert
} from '@mui/material';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { useAuth } from '../../contexts/AuthContext';

const HCAPTCHA_SITEKEY = process.env.REACT_APP_HCAPTCHA_SITEKEY;

const SignUp = ({ onToggleForm }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [accountExists, setAccountExists] = useState(false);
  const [success, setSuccess] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = useRef(null);
  
  const { signUp } = useAuth();

  const validatePassword = () => {
    if (password.length < 6) {
      return 'Password must be at least 6 characters';
    }
    if (password !== confirmPassword) {
      return 'Passwords do not match';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setAccountExists(false);
    setSuccess(false);
    
    // Validate password
    const passwordError = validatePassword();
    if (passwordError) {
      setError(passwordError);
      return;
    }
    
    setLoading(true);
    
    try {
      const { error } = await signUp(email, password, captchaToken);
      
      if (error) {
        throw error;
      }
      
      setSuccess(true);
    } catch (error) {
      const msg = String(error?.message || '').toLowerCase();
      if (
        msg.includes('already registered') ||
        msg.includes('already exists') ||
        msg.includes('user already')
      ) {
        setAccountExists(true);
        setError('An account with this email already exists. Please sign in instead.');
      } else {
        setError(error.message || 'Failed to create an account');
      }
    } finally {
      setLoading(false);
      captchaRef.current?.resetCaptcha();
      setCaptchaToken(null);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 4, maxWidth: 400, mx: 'auto', mt: 4 }}>
      <Typography variant="h5" component="h1" gutterBottom align="center">
        Create a Lexory Account
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {accountExists && (
        <Button
          fullWidth
          variant="outlined"
          sx={{ mb: 2 }}
          onClick={onToggleForm}
          disabled={loading}
        >
          Go to Sign In
        </Button>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Account created! Please check your email to confirm your registration.
        </Alert>
      )}
      
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <TextField
          margin="normal"
          required
          fullWidth
          id="email"
          label="Email Address"
          name="email"
          autoComplete="email"
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={loading || success}
        />
        <TextField
          margin="normal"
          required
          fullWidth
          name="password"
          label="Password"
          type="password"
          id="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={loading || success}
          helperText="Password must be at least 6 characters"
        />
        <TextField
          margin="normal"
          required
          fullWidth
          name="confirmPassword"
          label="Confirm Password"
          type="password"
          id="confirmPassword"
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          disabled={loading || success}
        />

        {HCAPTCHA_SITEKEY && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <HCaptcha
              ref={captchaRef}
              sitekey={HCAPTCHA_SITEKEY}
              onVerify={(token) => setCaptchaToken(token)}
              onExpire={() => setCaptchaToken(null)}
            />
          </Box>
        )}
        
        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          sx={{ mt: 3, mb: 2 }}
          disabled={loading || success || (HCAPTCHA_SITEKEY && !captchaToken)}
        >
          {loading ? <CircularProgress size={24} /> : 'Sign Up'}
        </Button>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
          <Link 
            component="button" 
            variant="body2" 
            onClick={onToggleForm}
            disabled={loading}
          >
            Already have an account? Sign In
          </Link>
        </Box>
      </Box>
    </Paper>
  );
};

export default SignUp;
